// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageHero, PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { ImageTextSection } from "@/components/ImageTextSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Images
import heroDaqVideo from "@/assets/video/dados.mp4";
import ctaImage from "@/assets/hero-contact.avif";
import daq1 from "@/assets/daq1.png";
import daqVideo from "@/assets/video/daq.mp4";
import heroImage from "@/assets/hero-daq.png";

export const Route = createFileRoute("/$lang/products/daq")({
  head: () => ({
    meta: [
      { title: i18n.t("products/daq:metaTitle", "DAQ_BIZU Data Acquisition System — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/daq:metaDesc",
          "DAQ_BIZU is a modular, high-precision data acquisition unit designed for rocket engine test benches, structural testing, and laboratory environments."
        ),
      },
      { property: "og:title", content: i18n.t("products/daq:metaTitle", "DAQ_BIZU Data Acquisition System — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/daq:metaOgDesc",
          "High-precision multi-sensor acquisition core designed to record thrust, pressure, temperature, and flight telemetry."
        ),
      },
      { property: "og:url", content: "/products/daq" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/daq" }],
  }),
  component: DAQ,
});

function DAQ() {
  const { t } = useTranslation("products/daq");

  const cards = [
    {
      number: "01",
      title: t("cards.c1Title", "Multi-Sensor Data Acquisition"),
      items: [
        t("cards.c1Bullet1", "4–20 mA pressure transmitters measured through a shunt resistor, converting current into voltage reading;"),
        t("cards.c1Bullet2", "Load cells through internal amplification or dedicated signal conditioning;"),
        t("cards.c1Bullet3", "High-resolution, low-frequency analog signals, depending on the A/D converter version used;"),
        t("cards.c1Bullet4", "Digital inputs for valve states, limit switches, interlocks and discrete events."),
      ],
    },
    {
      number: "02",
      title: t("cards.c2Title", "Modern Processing Architecture"),
      items: [
        t("cards.c2Bullet1", "Microcontroller with advanced connectivity, real-time data processing, local memory logging and transmission;"),
        t("cards.c2Bullet2", "Task-based architecture for acquisition, SD logging and communication, increasing system reliability."),
      ],
    },
    {
      number: "03",
      title: t("cards.c3Title", "Local Logging and Telemetry"),
      items: [
        t("cards.c3Bullet1", "Data recording on SD card for post-processing and traceability;"),
        t("cards.c3Bullet2", "Data transmission via CAN, UART/RS-485 or other buses, depending on project configuration."),
      ],
    },
    {
      number: "04",
      title: t("cards.c4Title", "Integration with Larger Systems"),
      items: [
        t("cards.c4Bullet1", "Easy interfacing with ground computers, PLCs, SDR telemetry systems and visualization software;"),
        t("cards.c4Bullet2", "Structured data architecture to simplify processing in Python, MATLAB, LabVIEW or equivalent tools."),
      ],
    },
  ];

  const typicalApplications = [
    {
      title: t("apps.a1Title", "Propulsion Test Benches (Liquid, Hybrid or Solid Propellant)"),
      bullets: [
        t("apps.a1Bullet1", "Simultaneous measurement of thrust, chamber and line pressures, component temperatures, valve positions and safety states."),
        t("apps.a1Bullet2", "Full test data recording for post-test analysis, video correlation and simulation comparison."),
      ],
    },
    {
      title: t("apps.a2Title", "Ground Support Systems for Launch and Integrated Testing"),
      bullets: [
        t("apps.a2Bullet1", "Monitoring of cryogenic or pressurized tanks;"),
        t("apps.a2Bullet2", "Supervision of limit switches, interlocks and safety permissives;"),
        t("apps.a2Bullet3", "Integration with command and telemetry systems."),
      ],
    },
    {
      title: t("apps.a3Title", "Structural and Laboratory Testing"),
      bullets: [
        t("apps.a3Bullet1", "Monitoring of strain, loads and displacements;"),
        t("apps.a3Bullet2", "Static and dynamic testing of structures, mechanical components and prototypes."),
      ],
    },
    {
      title: t("apps.a4Title", "Academic Environment"),
      bullets: [
        t("apps.a4Bullet1", "Undergraduate, scientific initiation and postgraduate projects;"),
        t("apps.a4Bullet2", "Practical classes in instrumentation, control and embedded systems, using hardware close to real-world space project standards."),
      ],
    },
  ];

  return (
    <PageShell>
      {/* HERO SECTION */}
      <PageHero
        title={t("heroTitle", "Data Aquisition System")}
        description={t("heroDesc", "The data acquisition platform designed for rockets, test benches, and laboratories.")}
        image={heroImage}
      />

      <ImageTextSection
        title={t("whatTitle", "What is DAQ_BIZU?")}
        subtitle={t("whatSubtitle", "In general terms, DAQ_BIZU is a modular data acquisition unit designed for:")}
        image={daq1}
        imagePosition="right"
        description={
          <>
            <ul className="space-y-4 ">
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("whatBullet1", "Rocket engine test benches (thrust, pressure, temperature, actuator position);")}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("whatBullet2", "Structural and vibration testing (strain gauges, load cells, displacement sensors);")}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("whatBullet3", "Ground support systems and launch data platforms (monitoring tanks, lines, valves and other sensors);")}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-accent mr-3 select-none font-bold text-lg leading-none">
                  &gt;
                </span>
                <span className="text-white/80 font-light text-base md:text-lg leading-relaxed">
                  {t("whatBullet4", "Research and education in universities (embedded systems, instrumentation and control laboratories).")}
                </span>
              </li>
            </ul>
            <p className="mt-5">
              {t("whatDesc", "It was designed to be the “measurement core” of your experiment: it receives sensor signals, digitizes them with high precision and makes the data available in an organized format for logging, visualization and telemetry.")}
            </p>
          </>
        }
      />

      <section className="bg-[#0b0b0b] py-24 text-white">
        <div className="container-bizu">
          <Reveal effect="up">
            <h2 className="display text-4xl md:text-5xl">{t("whyTitle", "Why Did We Create DAQ_BIZU?")}</h2>

            <p className="mt-6 text-base leading-relaxed text-white/70">
              {t("whyDesc", "Over the past few years, working with liquid rocket engine test benches, embedded systems and CubeSat projects, we identified a recurring pattern:")}
            </p>

            <ul className="mt-6 list-disc space-y-2 pl-6 text-base leading-relaxed text-white/70">
              <li>{t("whyBullet1", "Each team built its own electronics (sometimes improvised) to read sensors;")}</li>

              <li>
                {t("whyBullet2", "A mix of commercial boards, off-the-shelf modules and custom adaptations was common;")}
              </li>

              <li>
                {t("whyBullet3", "Integration with the rest of the system (CAN, serial, radio, software) was often complex and time-consuming;")}
              </li>

              <li>{t("whyBullet4", "There was a lack of standardization, traceability and proper documentation.")}</li>
            </ul>

            <p className="mt-6 text-base leading-relaxed text-white/70">
              {t("whyText", "This costs time, reliability and safety. A lost test due to data acquisition failure can mean hours (or days) of preparation wasted.")}
            </p>

            <blockquote className="mt-10 border-l-4 border-accent pl-6 text-lg italic leading-relaxed text-white/80">
              {t("whyQuote", "DAQ_BIZU was created as a response: a unified, robust and well-documented platform that concentrates what a rocket development team, test bench or laboratory needs to measure the physical world with confidence.")}
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* SECTION: SENSING & PROCESSING ARCHITECTURE */}
      <StackCardsSection cards={cards} daqVideo={daqVideo} t={t} />

      {/* SECTION 5: TYPICAL APPLICATIONS */}
      <section className="relative overflow-hidden bg-black py-28 text-white border-b border-accent">
        <div className="container-bizu">
          <Reveal effect="up" className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="display text-4xl uppercase md:text-5xl font-semibold tracking-wider text-white">
              {t("appsTitle", "TYPICAL APPLICATIONS")}
            </h2>
            <p className="mt-4 text-sm md:text-base text-white/70 font-light">
              {t("appsSubtitle", "Some scenarios where DAQ_BIZU fits:")}
            </p>
          </Reveal>

          <Reveal stagger className="grid gap-6 md:grid-cols-2">
            {typicalApplications.map((app) => (
              <div
                key={app.title}
                className="relative overflow-hidden rounded-2xl border border-white/5 bg-[#181818] p-8 transition duration-500 hover:bg-[#1f1f1f] hover:border-white/10"
              >
                <h3 className="text-xl text-white font-medium mb-6">{app.title}</h3>
                <ul className="space-y-4">
                  {app.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-white mr-3 select-none font-bold text-lg leading-none">
                        &bull;
                      </span>
                      <span className="text-white/80 font-light text-sm md:text-base leading-relaxed">
                        {bullet}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* SECTION 6: CHECK OUT OUR DAQ IN REAL OPERATION */}
      <section className="relative overflow-hidden bg-[#070707] py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mx-auto max-w-3xl text-center mb-16">
            <h2 className="display mt-5 text-4xl uppercase md:text-5xl">
              {t("operationTitle", "CHECK OUT OUR DAQ IN REAL OPERATION")}
            </h2>
          </Reveal>

          <Reveal effect="scale">
            <div className="relative h-[300px] md:h-[550px] overflow-hidden rounded-3xl border border-white/15">
              <video
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover opacity-90"
              >
                <source src={heroDaqVideo} type="video/mp4" />
              </video>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/25 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      <CtaSection
        image={ctaImage}
        title={t("ctaTitle", "Want to integrate BIZU_DAQ into your project?")}
        subtitle={t("ctaSubtitle", "Want to integrate BIZU_DAQ into your project?")}
        buttonText={t("ctaBtn", "Submit order request")}
        href="/contact"
      />
    </PageShell>
  );
}

function StackCardsSection({
  cards,
  daqVideo,
  t,
}: {
  cards: Array<{
    number: string;
    title: string;
    items: string[];
  }>;
  daqVideo: string;
  t: any;
}) {
  return (
    <section className="relative bg-black text-white border-b border-white/10 py-28">
      <div className="container-bizu">
        <div className="mb-12">
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-accent">
            {t("capabilitiesTitle", "Key Features")}
          </span>
          <h2 className="display mt-4 text-3xl md:text-5xl uppercase text-white leading-tight">
            {t("capabilitiesTitle", "Some typical capabilities of DAQ_BIZU include:")}
          </h2>
        </div>

        {/* Cards Grid - 2 columns on desktop */}
        <div className="grid gap-6 md:grid-cols-2 mb-16">
          {cards.map((card) => (
            <ArchitectureCard key={card.title} card={card} />
          ))}
        </div>

        {/* VIDEO */}
        <div className="relative h-[200px] md:h-[600px] w-full overflow-hidden rounded-3xl border border-white/10 bg-black shadow-[0_0_50px_rgba(45,48,214,0.15)]">
          {" "}
          <video autoPlay muted loop playsInline className="h-full w-full object-cover">
            <source src={daqVideo} type="video/mp4" />
          </video>
        </div>

        <p className="mx-auto mt-20 max-w-4xl text-center text-lg italic leading-relaxed text-white/60">
          {t("capabilitiesDesc", "Exact channel count, resolutions and interfaces may vary depending on the board version and specific customer project. However, the philosophy remains the same: flexible, robust and field-ready.")}
        </p>
      </div>
    </section>
  );
}

function ArchitectureCard({
  card,
}: {
  card: {
    number: string;
    title: string;
    items: string[];
  };
}) {
  return (
    <div className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b0b] p-8 transition duration-500 hover:bg-white/[0.04] shadow-[0_-15px_30px_rgba(0,0,0,0.75)] flex flex-col h-full">
      <div className="mb-4 text-lg font-bold text-accent tracking-wider">{card.number}</div>

      <h3 className="display mb-6 text-2xl text-white">{card.title}</h3>

      <ul className="space-y-3 text-sm leading-relaxed text-white/70 flex-1">
        {card.items.map((item) => (
          <li key={item} className="flex items-start">
            <span className="mr-3 select-none font-bold text-accent">&gt;</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
